import { useState, useEffect } from 'react';
import api from '@/utils/api';
import { ContentName } from '@/lib/types';

interface UseContentNamesReturn {
  contentNames: ContentName[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export default function useContentNames(workspaceId: string, contentTypeId: string): UseContentNamesReturn {
  const [contentNames, setContentNames] = useState<ContentName[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchContentNames = async () => {
    if (!workspaceId || !contentTypeId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/content-names/workspace/${workspaceId}/${contentTypeId}`);
      
      let data = response.data;
      if (data && typeof data === 'object') {
        // If response has a data property, use it
        if (Array.isArray(data.data)) {
          data = data.data;
        }
        // If response has a results property, use it
        else if (Array.isArray(data.results)) {
          data = data.results;
        }
        // If response has a content property, use it
        else if (Array.isArray(data.content)) {
          data = data.content;
        }
        // If the response itself is not an array, try to extract array from it
        else if (!Array.isArray(data)) {
          console.warn('API response is not an array:', data);
          data = [];
        }
      }
      
      // Ensure we always have an array and add IDs if missing
      const contentNamesArray = Array.isArray(data) ? data : [];
      const contentNamesWithIds = contentNamesArray.map((item, index) => ({
        ...item,
        id: item.id ?? index + 1, // Use existing ID or generate from index
      }));
      setContentNames(contentNamesWithIds);
    } catch (err) {
      setError('Failed to fetch content names');
      console.error('Error fetching content names:', err);
      setContentNames([]); // Ensure we have an empty array on error
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContentNames();
  }, [workspaceId, contentTypeId]);

  return {
    contentNames,
    isLoading,
    error,
    refetch: fetchContentNames,
  };
}
